export type BuyerReturnState =
  | 'AWAITING_RETURN'
  | 'RECEIVED'
  | 'INSPECTION_HOLD'
  | 'READY_TO_RESTOCK'
  | 'RESTOCKED'
  | 'CLOSED_NO_RETURN';

export const BUYER_REFUND_STATES = [
  'NOT_RECORDED',
  'PENDING',
  'PARTIAL',
  'REFUNDED',
  'NOT_REQUIRED',
] as const;
export type BuyerRefundState = (typeof BUYER_REFUND_STATES)[number];
export interface BuyerReturn {
  returnId: string;
  channel: 'EBAY_US' | 'OTHER';
  orderId: string;
  lineItemId: string;
  cardId: string;
  title: string;
  marketplaceListingId?: string;
  externalListingId?: string;
  reason: string;
  reference?: string;
  state: BuyerReturnState;
  refundState: BuyerRefundState;
  refundReference?: string;
  holdingLocation?: string;
  inspectionNotes?: string;
  restockedBatchId?: string;
  consignmentReviewRequired: boolean;
  financeReviewReference?: string;
  revision: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}
export interface BuyerReturnEvent {
  action: string;
  actorId: string;
  createdAt: string;
  revision: number;
  note: string;
}
export interface BuyerReturnDetail {
  item: BuyerReturn;
  events: BuyerReturnEvent[];
  imageUrl?: string;
}
export interface BuyerReturnSaleLine {
  lineItemId: string;
  cardId?: string;
  title: string;
  quantity: number;
}
export interface BuyerReturnPage {
  items: BuyerReturn[];
  cursor?: string;
}
export interface OpenBuyerReturn {
  channel: 'EBAY_US' | 'OTHER';
  orderId: string;
  lineItemId: string;
  cardId?: string;
  reason: string;
  reference?: string;
}
export type BuyerReturnAction =
  | 'RECEIVE'
  | 'ACCEPT_INSPECTION'
  | 'HOLD_INSPECTION'
  | 'RESTOCK'
  | 'CLOSE_NO_RETURN'
  | 'RECORD_REFUND'
  | 'REVIEW_FINANCE';
export interface MutateBuyerReturn {
  returnId: string;
  revision: number;
  action: BuyerReturnAction;
  note: string;
  batchId?: string;
  refundState?: BuyerRefundState;
}

export function buyerReturnActionAllowed(
  state: BuyerReturnState,
  action: BuyerReturnAction,
): boolean {
  switch (action) {
    case 'RECEIVE':
      return state === 'AWAITING_RETURN';
    case 'ACCEPT_INSPECTION':
    case 'HOLD_INSPECTION':
      return ['RECEIVED', 'INSPECTION_HOLD', 'READY_TO_RESTOCK'].includes(state);
    case 'RESTOCK':
      return state === 'READY_TO_RESTOCK';
    case 'CLOSE_NO_RETURN':
      return state === 'AWAITING_RETURN';
    case 'RECORD_REFUND':
      return true;
    case 'REVIEW_FINANCE':
      return ['RESTOCKED', 'CLOSED_NO_RETURN'].includes(state);
    default:
      return false;
  }
}

/** Only the exact historical sale can stop blocking relisting after physical restock. */
export function isRestockedBuyerReturnListing(listing: {
  observedState?: unknown;
  buyerReturnRestockedAt?: unknown;
  buyerReturnOrderId?: unknown;
  buyerReturnLineItemId?: unknown;
  soldOrderId?: unknown;
  soldLineItemId?: unknown;
}): boolean {
  return (
    ['SOLD', 'OBSERVED_SOLD', 'FULFILLED'].includes(String(listing.observedState)) &&
    typeof listing.buyerReturnRestockedAt === 'string' &&
    typeof listing.soldOrderId === 'string' &&
    typeof listing.soldLineItemId === 'string' &&
    listing.buyerReturnOrderId === listing.soldOrderId &&
    listing.buyerReturnLineItemId === listing.soldLineItemId
  );
}
